import React, { FC, useEffect } from 'react';
import { ImageSectionProps } from '@components/v2/ImageSection/index';
import CarGallery from '@components/CarGallery';
import MarketingSticker from '@components/MarketingSticker';
import { IPromo } from 'src/interfaces/IOfferItem';
import { findPromoByPageAndType, EContextPage } from '@utils/Promo.utils';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { FEATURES_LIST } from 'src/context/featureSwitchApp';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';
import { Icons } from '@components/Icon';
import Icon from '@components/Icon';
import { useSelector } from 'react-redux';
import { ImageViewType } from '../../../constants/enums';
import { isBrandAC, ROLES } from 'src/constants/main';
import TitleWithBenefits from '@components/v2/TitleWithBenefits';
import { useCarStockJourneyCheck } from '@hooks/useCarStockJourneyCheck';
import useTranslations from '@hooks/useTranslations';
import { formatLeadTime } from '@utils/Date.utils';
import { useOrdelPanelLogic } from '@hooks/useOrdelPanelLogic';
import { CarJourneyType } from 'src/services';
import { dealerTemplateId } from '@components/FAQs';
import { AccessibleButton } from '@components/Button/AccessibleButton';
import { Link } from '@components/v2/link';
import { CAR_GALLERY_ID } from '@hooks/useOrderPanelHeight';
import { useGetGalleryImages } from '@hooks/useGetGalleryImages';
import { useGetDealerInformation } from '@hooks/useGetDealerInformation';
import { Redux } from 'src/redux/redux.interface';
import TrimSelectorDuck from 'src/redux/trimSelector/trimSelector.duck';
import { breakpoints } from '../../../styles/theme';

export const ImageSectionTemplate: FC<ImageSectionProps> = ({ className, isBasket, isTrim }) => {
    const { t } = useTranslations();
    const { offers } = useCarDetailsDuck();

    const { carDetails, currentDeal } = useCarDetailsDuck();
    const { isStockJourney } = useCarStockJourneyCheck();

    const filteredStockTrims = useSelector((state: Redux) => TrimSelectorDuck.getFilteredStockTrims(state));

    const isPromoEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_PROMO_ENABLED);
    const isAllowedRedirectToMTO = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_REDIRECT_TO_MTO_ENABLED);
    const isStockJourneyAllowed = useFeatureSwitchEnabled(
        FEATURES_LIST.FEATURE_SWITCH_ALLOWED_CAR_JOURNEYS,
        CarJourneyType.STOCK
    );

    const { redirectToMTO, redirectToStock } = useOrdelPanelLogic();
    const {
        isExterior,
        isInterior,
        imageViewType: imageType,
        selectedImageViewType,
        isSelectedExterior,
        isSelectedInterior,
        setImageViewType,
    } = useGetGalleryImages({ allowDefaultConfigurationImages: isTrim });
    const getPromoTypeByPage = () => {
        if (isBasket) return EContextPage.basket;
        if (isTrim) return EContextPage.trim;

        return EContextPage.config;
    };

    const isMarketingStickerEnabled = isPromoEnabled && findPromoByPageAndType(offers, getPromoTypeByPage());

    const scrollToDealer = () => {
        const element = document.getElementById(dealerTemplateId);

        return element.scrollIntoView();
    };

    const hasDealer = currentDeal?.fullProductConfiguration?.dealers?.length > 0 || currentDeal?.dealerId;

    const { zipCode, city, street, dealerName } = useGetDealerInformation();

    useEffect(() => {
        window.addEventListener('resize', () => {
            const carGallery = document.getElementById(CAR_GALLERY_ID);
            const carDescription = document.getElementById('vehicleImageSection');

            if (window.innerWidth >= breakpoints.md && carGallery && carDescription && hasDealer) {
                carGallery.style.height = `${(carDescription?.offsetHeight ?? 0) + 20}px`;
            } else if (carGallery) {
                carGallery.style.height = 'auto';
            }
        });
    }, []);

    return (
        <div className={className} id={CAR_GALLERY_ID}>
            <div className="switcherWithMarketingRow">
                {(isExterior || isInterior) && (
                    <div className="toggle">
                        {isExterior && (
                            <AccessibleButton tabIndex={0} onClick={() => setImageViewType(ImageViewType.EXTERIOR)}>
                                <span
                                    className={`ext ${isSelectedExterior ? 'active' : ''}`}
                                    data-testid="TESTING_IMAGE_SECTION_EXTERIOR_SWITCHER"
                                >
                                    <Icon
                                        height={10}
                                        altId="svgImgExteriorSwitcher"
                                        altText={t('toggleView.exterior')}
                                        role={ROLES.IMG}
                                        name={isBrandAC ? Icons.ExteriorBold : Icons.ExteriorV2}
                                    />
                                </span>
                            </AccessibleButton>
                        )}
                        {isInterior && (
                            <AccessibleButton tabIndex={0} onClick={() => setImageViewType(ImageViewType.INTERIOR)}>
                                <span
                                    className={`int ${isSelectedInterior ? 'active' : ''}`}
                                    data-testid="TESTING_IMAGE_SECTION_INTERIOR_SWITCHER"
                                >
                                    <Icon
                                        height={10}
                                        altId="svgImgInteriorSwitcher"
                                        altText={t('toggleView.interior')}
                                        role={ROLES.IMG}
                                        name={isBrandAC ? Icons.InteriorBold : Icons.InteriorV2}
                                    />
                                </span>
                            </AccessibleButton>
                        )}
                    </div>
                )}
                <div className="marketing">
                    {isMarketingStickerEnabled && (
                        <MarketingSticker
                            data={isMarketingStickerEnabled as unknown as Array<IPromo>}
                            page={EContextPage.config}
                        />
                    )}
                </div>
            </div>
            <div className={selectedImageViewType}>
                <CarGallery
                    arrows={false}
                    defaultArrows
                    dots={false}
                    setSmallImage={isBasket}
                    imageSet={imageType}
                    // Trim page has default configuration images
                    allowDefaultConfigurationImages={isTrim}
                />
            </div>
            {isBasket && (
                <div className="vehicle" id="vehicleImageSection">
                    <TitleWithBenefits withBenefits={false} isBasket={isBasket} />
                    <div className="delivery">{formatLeadTime(carDetails.leadTimeDate)}</div>
                    {isStockJourney
                        ? isAllowedRedirectToMTO && (
                              <div onClick={redirectToMTO} className="redirectLink">
                                  <span className="redirectText">{t('orderPanel.mtoRedirect.link')}</span>
                              </div>
                          )
                        : isStockJourneyAllowed &&
                          filteredStockTrims.length > 0 && (
                              <div onClick={redirectToStock} className="redirectLink">
                                  <span className="redirectText">
                                      {t('orderPanel.internalStockRedirect', {
                                          model: carDetails.model.title,
                                          count: filteredStockTrims.length,
                                      })}
                                  </span>
                              </div>
                          )}
                    {hasDealer && (
                        <div className="dealer">
                            <div className="label">
                                <Icon width={20} height={20} name={Icons.MapPoint} />
                                <span>{t('basket.dealer.label')}</span>
                            </div>
                            <div className="title">{dealerName}</div>
                            <div className="address">
                                {street}, {zipCode} {city}
                            </div>
                            <Link
                                style={{ marginLeft: '28px' }}
                                isUnderline
                                txt={t('basket.dealer.more')}
                                onClick={scrollToDealer}
                            >
                                <button className="link" onClick={scrollToDealer}>
                                    {t('basket.dealer.more')}
                                </button>
                            </Link>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
